import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { AccionDeAthena, AthenaAdjunto, AthenaHiloEnLista, AthenaMensaje, DestinoDeAdjunto } from '@conversia/shared-types';
import { Building2, FileUp, PanelRightClose, PanelRightOpen, SquarePen } from 'lucide-react';
import * as athenaApi from '@/api/athena.api';
import type { HiloConMensajes } from '@/api/athena.api';
import * as asuntosApi from '@/api/asuntos.api';
import * as inspectorApi from '@/api/inspector.api';
import { listarCartera } from '@/api/cartera.api';
import { ApiError } from '@/api/client';
import { listarProyectos } from '@/api/proyectos.api';
import { CampoDeAthena, type ArchivoEnCola, type ManejoDelCampo } from '@/components/athena/CampoDeAthena';
import {
  AvatarDeAthena,
  BurbujaDeAthena,
  Escribiendo,
  ResumenDelDia,
  Sugerencias,
  type EstadoDeAcciones,
  type IdDeSugerencia,
} from '@/components/athena/ConversacionDeAthena';
import { HistorialDeAthena } from '@/components/athena/HistorialDeAthena';
import { VentanaDeFicha } from '@/components/cartera/VentanaDeFicha';
import { Modal } from '@/components/ui/Modal';
import { usePreferencia } from '@/hooks/usePreferencia';
import { useToast } from '@/hooks/useToast';
import { useVeRuta } from '@/hooks/useMenu';
import { MAX_ADJUNTOS, TOPE_DE_ADJUNTO, archivosAceptables, saludoDe, traeArchivos } from '@/lib/athena-hilos';
import { cn } from '@/lib/cn';
import { unaDe } from '@/lib/preferencias';
import { useAuthStore } from '@/store/auth.store';

/**
 * ATHENA — la pantalla donde se conversa con ella (tanda 6).
 *
 * Kervens, 7-oct: *«se ve como una pantalla blanca completamente, necesito que estructures mejor
 * geométricamente cómo sería este chat… debe tener un historial a mano derecha como si fuese
 * 100 % un mini ChatGPT dentro de mi sistema, que lleve historial, que yo le pueda subir
 * documentos, PDF y los lleve a los lugares correspondientes, fotos, mandarle un audio».*
 *
 * Tres zonas a todo el alto: la conversación al centro (columna de lectura), el campo de
 * escribir pegado abajo y el historial a la derecha (plegable; en el teléfono, un panel que
 * entra desde la derecha).
 *
 * ═══ AHORA TIENE MEMORIA ═══
 * Antes la charla vivía en la pantalla y se perdía al salir. Ahora cada conversación es un hilo
 * guardado en el servidor, y el hilo abierto va en la URL (`/athena?hilo=…`): recargar o
 * compartir el enlace abre el mismo. Un hilo se crea al mandar el PRIMER mensaje, no al tocar
 * «Nueva conversación»: el historial no se llena de conversaciones vacías.
 *
 * ═══ NO HAY BOTÓN DE ENVIAR A WHATSAPP ═══
 * Athena redacta y propone; mandar lo hace la persona. Sus mensajes traen «Copiar» y botones
 * que abren una pantalla o llevan un archivo a su lugar, siempre al tocarlos. No existe la ruta
 * para que mande nada sola.
 */

const SIN_NOMBRE = 'Conversación nueva';
const sanearHistorial = unaDe(['abierto', 'cerrado'] as const, 'abierto');
const FOCO = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-600';
const DE_CABECERA = cn(
  'inline-flex h-11 min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-900/5 hover:text-slate-900 md:h-9 md:min-w-[36px] dark:text-slate-300 dark:hover:bg-white/10 dark:hover:text-white',
  FOCO,
);

/** Un mensaje del usuario que todavía no llegó al servidor (o que no llegó). */
interface Pendiente {
  clave: string;
  /** null: de una conversación nueva, cuyo hilo aún no se crea. */
  hiloId: string | null;
  texto: string;
  adjuntos: AthenaAdjunto[];
  fallo: boolean;
  createdAt: string;
}

/** Un botón de un mensaje: de cuál mensaje y cuál de sus botones. */
interface Tocado {
  mensajeId: string;
  indice: number;
}

let cuenta = 0;
const nuevaClave = () => `local-${Date.now().toString(36)}-${++cuenta}`;
const llaveDeHilo = (id: string | null) => ['athena', 'hilo', id] as const;
const LLAVE_DE_HILOS = ['athena', 'hilos'] as const;

const PREGUNTA_DE_PROYECTO: Record<DestinoDeAdjunto, string> = {
  brochure: '¿De cuál proyecto es el brochure?',
  foto: '¿De cuál proyecto es la foto?',
  video: '¿De cuál proyecto es el video?',
};

/** «Llevar» un archivo: de cuál proyecto es. La lista es la misma de la pantalla «Proyectos». */
function SelectorDeProyecto({ destino, ocupado, onElegir, onCerrar }: { destino: DestinoDeAdjunto; ocupado: boolean; onElegir: (proyectoId: string) => void; onCerrar: () => void }) {
  const navegar = useNavigate();
  const { data, isPending, isError, refetch } = useQuery({ queryKey: ['proyectos'], queryFn: listarProyectos });
  const proyectos = data?.proyectos ?? [];
  return (
    <Modal open onClose={onCerrar} title={PREGUNTA_DE_PROYECTO[destino]} description="Elige uno y lo pongo ahí." size="md">
      {isPending ? (
        <div aria-hidden className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 rounded-xl bg-slate-100 motion-safe:animate-pulse dark:bg-slate-800" />
          ))}
        </div>
      ) : isError ? (
        <p className="text-sm text-slate-600 dark:text-slate-300">
          No pude cargar tus proyectos.{' '}
          <button type="button" onClick={() => void refetch()} className={cn('rounded font-semibold text-brand-700 underline underline-offset-2 dark:text-brand-300', FOCO)}>
            Reintentar
          </button>
        </p>
      ) : proyectos.length === 0 ? (
        <div className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
          <p>Todavía no tienes proyectos. Crea el proyecto primero y vuelve: el archivo se queda guardado en esta conversación.</p>
          <button type="button" onClick={() => navegar('/proyectos')} className={cn('inline-flex min-h-[44px] items-center rounded-lg bg-brand-600 px-4 font-semibold text-white hover:bg-brand-700 md:min-h-[40px]', FOCO)}>
            Abrir Proyectos
          </button>
        </div>
      ) : (
        <ul aria-label="Tus proyectos" className="space-y-1.5">
          {proyectos.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                disabled={ocupado}
                data-proyecto={p.id}
                onClick={() => onElegir(p.id)}
                className={cn(
                  'flex min-h-[56px] w-full items-center gap-3 rounded-xl border border-surface-border bg-surface px-3 py-2 text-left transition-colors',
                  'enabled:hover:border-brand-300 enabled:hover:bg-brand-50 disabled:opacity-60 dark:enabled:hover:border-brand-500/60 dark:enabled:hover:bg-brand-900/30',
                  FOCO,
                )}
              >
                <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-200">
                  <Building2 className="h-[18px] w-[18px]" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-slate-900 dark:text-slate-50">{p.nombre}</span>
                  <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                    {/* Un brochure no se suma: reemplaza al que hubiera. Se dice antes de tocar. */}
                    {[p.zona, destino === 'brochure' && p.brochureUrl ? 'ya tiene brochure: este lo reemplaza' : ''].filter(Boolean).join(' · ') || 'Sin zona'}
                  </span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

export default function Athena() {
  const [params, setParams] = useSearchParams();
  const hiloId = params.get('hilo');
  const qc = useQueryClient();
  const toast = useToast();
  const veAjustes = useVeRuta('/inspector/ajustes');
  // `useToast` devuelve un objeto nuevo en cada pintada: por referencia, para no rehacer `mandar` cada vez.
  const avisar = useRef(toast);
  avisar.current = toast;
  const navegar = useNavigate();
  const nombre = useAuthStore((s) => s.user?.name);

  // El historial: en escritorio, abierto o plegado según lo que la persona dejó (la sigue al
  // teléfono y a otra laptop); en el teléfono es un panel que se abre a pedido y arranca cerrado.
  const [historial, setHistorial] = usePreferencia<'abierto' | 'cerrado'>('athena.historial', 'abierto', sanearHistorial);
  const [panelEnTelefono, setPanelEnTelefono] = useState(false);

  const [texto, setTexto] = useState('');
  const [cola, setCola] = useState<ArchivoEnCola[]>([]);
  const [pendientes, setPendientes] = useState<Pendiente[]>([]);
  const [soltando, setSoltando] = useState(false);
  // Los botones de los mensajes. Viven aquí: el servidor no guarda qué pregunta ya se contestó.
  const [marcas, setMarcas] = useState<Record<string, Pick<EstadoDeAcciones, 'resuelto' | 'hechas'>>>({});
  const [ocupada, setOcupada] = useState<Tocado | null>(null);
  const [porConfirmar, setPorConfirmar] = useState<Tocado | null>(null);
  const [llevando, setLlevando] = useState<(Tocado & { adjuntoId: string; destino: DestinoDeAdjunto }) | null>(null);
  const [ficha, setFicha] = useState<(Tocado & { contactId: string }) | null>(null);

  const campo = useRef<ManejoDelCampo>(null);
  const rollo = useRef<HTMLDivElement>(null);

  // ───────────── datos ─────────────
  const hilos = useQuery({ queryKey: LLAVE_DE_HILOS, queryFn: athenaApi.listarHilos });
  const hilo = useQuery({
    queryKey: llaveDeHilo(hiloId),
    queryFn: () => athenaApi.leerHilo(hiloId ?? ''),
    enabled: Boolean(hiloId),
    // Un hilo que no existe no va a existir al segundo intento.
    retry: (veces, error) => !(error instanceof ApiError && error.status === 404) && veces < 1,
    // Al volver a la pestaña no se recarga solo: llegaría a medio envío y la pregunta saldría dos veces.
    refetchOnWindowFocus: false,
  });
  const asuntos = useQuery({ queryKey: ['asuntos', 'athena'], queryFn: () => asuntosApi.getAsuntos({ limite: 300 }) });
  const senales = useQuery({ queryKey: ['inspector', 'senales'], queryFn: inspectorApi.getSenales });
  // La ficha de un cliente sale de la misma lista que «Clientes»: se pide al tocar el primer «Ver ficha».
  const cartera = useQuery({ queryKey: ['cartera'], queryFn: listarCartera, enabled: Boolean(ficha) });

  // El «ahora» de las horas y de los grupos del historial. Avanza cada minuto: con la pantalla
  // abierta de un día para otro, «Hoy» deja de ser hoy a medianoche.
  const [ahora, setAhora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAhora(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const guardados = useMemo(() => (hiloId ? (hilo.data?.mensajes ?? []) : []), [hiloId, hilo.data]);
  const enVuelo = pendientes.filter((p) => p.hiloId === hiloId);
  const contestando = pendientes.some((p) => !p.fallo);
  const contestandoAqui = enVuelo.some((p) => !p.fallo);
  const titulo = (hiloId && (hilos.data?.hilos.find((h) => h.id === hiloId)?.titulo ?? hilo.data?.hilo.titulo)) || SIN_NOMBRE;
  // La pantalla de bienvenida: una conversación nueva sin nada mandado (o un hilo guardado sin mensajes).
  const vacia = guardados.length === 0 && enVuelo.length === 0 && (!hiloId || hilo.isSuccess);

  // Un enlace a una conversación que ya se borró (o de otra persona): se dice y se empieza una nueva.
  const noExiste = hilo.error instanceof ApiError && hilo.error.status === 404;
  useEffect(() => {
    if (!noExiste) return;
    toast.warning('Esa conversación ya no existe', 'Empezamos una nueva.');
    setParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noExiste]);

  // La conversación baja sola al último mensaje. Al abrir un hilo, de golpe; cuando llega uno
  // nuevo, con suavidad (si la persona no pidió menos movimiento).
  const anterior = useRef<{ hilo: string | null; cuantos: number }>({ hilo: hiloId, cuantos: 0 });
  useEffect(() => {
    const el = rollo.current;
    if (!el) return;
    const cuantos = guardados.length + enVuelo.length;
    const seguia = anterior.current.hilo === hiloId && anterior.current.cuantos > 0;
    anterior.current = { hilo: hiloId, cuantos };
    const suave = seguia && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollTo({ top: el.scrollHeight, behavior: suave ? 'smooth' : 'auto' });
  }, [hiloId, guardados.length, enVuelo.length, contestandoAqui]);

  // El panel del teléfono se cierra con Escape.
  useEffect(() => {
    if (!panelEnTelefono) return;
    const alTeclear = (e: KeyboardEvent) => e.key === 'Escape' && setPanelEnTelefono(false);
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [panelEnTelefono]);

  // ───────────── mandar ─────────────
  const mandar = useCallback(
    async (p: Pendiente) => {
      let id = p.hiloId;
      try {
        if (!id) {
          const creado = (await athenaApi.crearHilo()).hilo;
          id = creado.id;
          qc.setQueryData<HiloConMensajes>(llaveDeHilo(creado.id), { hilo: creado, mensajes: [] });
          setPendientes((ps) => ps.map((x) => (x.clave === p.clave ? { ...x, hiloId: creado.id } : x)));
          // Solo si la persona sigue en la conversación nueva: si mientras tanto abrió otra, no se la cambia.
          setParams((antes) => (antes.get('hilo') ? antes : new URLSearchParams({ hilo: creado.id })), { replace: true });
        }
        const r = await athenaApi.mandarMensaje(id, {
          ...(p.texto ? { texto: p.texto } : {}),
          ...(p.adjuntos.length ? { adjuntos: p.adjuntos.map((a) => a.id) } : {}),
        });
        qc.setQueryData<HiloConMensajes>(llaveDeHilo(id), (antes) => {
          if (!antes) return antes;
          const ya = new Set(antes.mensajes.map((m) => m.id));
          return { ...antes, mensajes: [...antes.mensajes, ...[r.pregunta, r.respuesta].filter((m) => !ya.has(m.id))] };
        });
        setPendientes((ps) => ps.filter((x) => x.clave !== p.clave));
        // El título sale de la primera pregunta y la vista previa cambia: el historial se vuelve a pedir.
        void qc.invalidateQueries({ queryKey: LLAVE_DE_HILOS });
      } catch (e) {
        // La pregunta se queda en pantalla, marcada: reescribirla por un fallo que no fue de la
        // persona sería castigarla. ponytail: si el servidor sí la guardó y lo que se perdió fue
        // la respuesta, «Reintentar» la manda otra vez; al recargar se verían las dos.
        const suHilo = id;
        setPendientes((ps) => ps.map((x) => (x.clave === p.clave ? { ...x, hiloId: suHilo ?? x.hiloId, fallo: true } : x)));
        // Y se dice por qué: «No se envió» a secas no deja saber si conviene reintentar (revisión de Codex).
        avisar.current.error('No se envió', e instanceof Error ? e.message : undefined);
      }
    },
    [qc, setParams],
  );

  const despachar = (dicho: string, adjuntos: AthenaAdjunto[]) => {
    const limpio = dicho.trim();
    if ((!limpio && adjuntos.length === 0) || contestando) return false;
    const p: Pendiente = { clave: nuevaClave(), hiloId, texto: limpio, adjuntos, fallo: false, createdAt: new Date().toISOString() };
    setPendientes((ps) => [...ps, p]);
    void mandar(p);
    return true;
  };

  const listos = cola.flatMap((a) => (a.estado === 'listo' && a.adjunto ? [a.adjunto] : []));
  const subiendo = cola.some((a) => a.estado === 'subiendo');
  const puedeEnviar = !subiendo && !contestando && (texto.trim().length > 0 || listos.length > 0);

  const enviar = () => {
    if (!puedeEnviar || !despachar(texto, listos)) return;
    setTexto('');
    setCola([]);
  };

  const reintentar = (p: Pendiente) => {
    if (contestando) return;
    const otra = { ...p, fallo: false };
    setPendientes((ps) => ps.map((x) => (x.clave === p.clave ? otra : x)));
    void mandar(otra);
  };

  // ───────────── archivos ─────────────
  /** Cada archivo sube al momento y aparece sobre el campo; `alLlegar` es para la grabación. */
  const subir = (archivos: File[], alLlegar?: (adjunto: AthenaAdjunto, clave: string) => void) => {
    // Los que fallaron al subir no ocupan sitio: no van a salir con el mensaje.
    const ocupados = cola.filter((a) => a.estado !== 'error').length;
    const { aceptados, rechazados } = archivosAceptables(archivos, TOPE_DE_ADJUNTO, MAX_ADJUNTOS - ocupados);
    for (const r of rechazados) toast.warning(`No subí «${r.nombre}»`, r.motivo);
    const nuevos = aceptados.map((archivo) => ({ clave: nuevaClave(), archivo }));
    if (!nuevos.length) return;
    setCola((c) => [...c, ...nuevos.map(({ clave, archivo }) => ({ clave, nombre: archivo.name, tamano: archivo.size, estado: 'subiendo' as const }))]);
    for (const { clave, archivo } of nuevos) {
      athenaApi.subirAdjunto(archivo).then(
        ({ adjunto }) => {
          // Si la persona lo quitó mientras subía, ya no está en la cola y esto no cambia nada.
          setCola((c) => c.map((x) => (x.clave === clave ? { ...x, nombre: adjunto.nombre, estado: 'listo', adjunto } : x)));
          alLlegar?.(adjunto, clave);
        },
        (e: unknown) => setCola((c) => c.map((x) => (x.clave === clave ? { ...x, estado: 'error', error: e instanceof Error ? e.message : undefined } : x))),
      );
    }
  };

  /** Una grabación: si el servidor la pudo oír, lo dicho va al campo para revisarlo; si no, queda como audio adjunto. */
  const subirGrabacion = (archivo: File) =>
    subir([archivo], (adjunto, clave) => {
      if (!adjunto.transcripcion) return;
      const dicho = adjunto.transcripcion.trim();
      setTexto((t) => (t.trim() ? `${t.trimEnd()} ${dicho}` : dicho));
      setCola((c) => c.filter((x) => x.clave !== clave));
      campo.current?.enfocar();
    });

  // ───────────── los botones de Athena ─────────────
  const refrescarHilo = () => {
    void qc.invalidateQueries({ queryKey: llaveDeHilo(hiloId) });
    void qc.invalidateQueries({ queryKey: LLAVE_DE_HILOS });
  };

  const alTocar = (mensaje: AthenaMensaje, accion: AccionDeAthena, indice: number) => {
    const tocado = { mensajeId: mensaje.id, indice };
    setPorConfirmar(null);
    if (accion.tipo === 'ir') {
      // Solo pantallas de este panel: una ruta que no empiece por «/» no se abre.
      if (/^\/(?!\/)/.test(accion.ruta)) navegar(accion.ruta);
    } else if (accion.tipo === 'ficha') setFicha({ ...tocado, contactId: accion.contactId });
    else if (accion.tipo === 'llevar') setLlevando({ ...tocado, adjuntoId: accion.adjuntoId, destino: accion.destino });
    else if (accion.tipo === 'importarChat') setPorConfirmar(tocado);
    else setMarcas((m) => ({ ...m, [mensaje.id]: { ...m[mensaje.id], resuelto: true } }));
  };

  const importar = (mensaje: AthenaMensaje, accion: AccionDeAthena, indice: number) => {
    if (accion.tipo !== 'importarChat') return;
    setPorConfirmar(null);
    setOcupada({ mensajeId: mensaje.id, indice });
    athenaApi
      .importarChat(accion.adjuntoId, { ...(hiloId ? { hiloId } : {}), ...(accion.yoSoy ? { yoSoy: accion.yoSoy } : {}) })
      .then(
        () => {
          // Un chat se importa una vez: sus botones se apagan. Lo que pasó lo deja dicho el servidor en el hilo.
          setMarcas((m) => ({ ...m, [mensaje.id]: { ...m[mensaje.id], resuelto: true, hechas: [...(m[mensaje.id]?.hechas ?? []), indice] } }));
          refrescarHilo();
        },
        (e: unknown) => toast.error('No se pudo importar el chat', e instanceof Error ? e.message : undefined),
      )
      .finally(() => setOcupada(null));
  };

  const llevar = (proyectoId: string) => {
    if (!llevando) return;
    const { mensajeId, indice, adjuntoId, destino } = llevando;
    setOcupada({ mensajeId, indice });
    athenaApi
      .llevarAdjunto(adjuntoId, { destino, proyectoId, ...(hiloId ? { hiloId } : {}) })
      .then(
        () => {
          setMarcas((m) => ({ ...m, [mensajeId]: { ...m[mensajeId], hechas: [...(m[mensajeId]?.hechas ?? []), indice] } }));
          setLlevando(null);
          refrescarHilo();
          void qc.invalidateQueries({ queryKey: ['proyectos'] });
        },
        // El selector se queda abierto: si ese proyecto ya está lleno, se elige otro.
        (e: unknown) => toast.error('No se pudo llevar el archivo', e instanceof Error ? e.message : undefined),
      )
      .finally(() => setOcupada(null));
  };

  // La ficha: la fila del cliente sale de la lista de Clientes. Si no está (lo borraron) o no cargó, se dice.
  const fila = ficha ? cartera.data?.filas.find((f) => f.contactId === ficha.contactId) : undefined;
  const fichaSinFila = Boolean(ficha) && !fila && (cartera.isSuccess || cartera.isError);
  useEffect(() => {
    if (!fichaSinFila) return;
    if (cartera.isError) toast.error('No pude abrir la ficha', cartera.error instanceof Error ? cartera.error.message : undefined);
    else toast.warning('Ese cliente ya no está en tu lista de Clientes');
    setFicha(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fichaSinFila]);
  const abriendoFicha = ficha && !fila ? ficha : null;

  const estadoDe = (mensajeId: string): EstadoDeAcciones => {
    const corre = ocupada ?? abriendoFicha;
    return {
      ...marcas[mensajeId],
      ocupada: corre?.mensajeId === mensajeId ? corre.indice : null,
      porConfirmar: porConfirmar?.mensajeId === mensajeId ? porConfirmar.indice : null,
    };
  };

  // ───────────── el historial ─────────────
  const abrir = (id: string | null) => {
    setPanelEnTelefono(false);
    setPorConfirmar(null);
    setParams(id ? { hilo: id } : {});
    if (!id) campo.current?.enfocar();
  };

  const renombrar = (id: string, nuevo: string) => {
    // Se ve al instante; si el servidor dice que no, vuelve a lo que diga él.
    qc.setQueryData<{ hilos: AthenaHiloEnLista[] }>(LLAVE_DE_HILOS, (antes) => antes && { hilos: antes.hilos.map((h) => (h.id === id ? { ...h, titulo: nuevo } : h)) });
    qc.setQueryData<HiloConMensajes>(llaveDeHilo(id), (antes) => antes && { ...antes, hilo: { ...antes.hilo, titulo: nuevo } });
    athenaApi.renombrarHilo(id, nuevo).catch((e: unknown) => {
      toast.error('No se pudo renombrar', e instanceof Error ? e.message : undefined);
      void qc.invalidateQueries({ queryKey: ['athena'] });
    });
  };

  const borrar = (id: string) => {
    athenaApi.borrarHilo(id).then(
      () => {
        qc.setQueryData<{ hilos: AthenaHiloEnLista[] }>(LLAVE_DE_HILOS, (antes) => antes && { hilos: antes.hilos.filter((h) => h.id !== id) });
        setPendientes((ps) => ps.filter((p) => p.hiloId !== id));
        if (id === hiloId) setParams({}, { replace: true });
        qc.removeQueries({ queryKey: llaveDeHilo(id) });
      },
      (e: unknown) => toast.error('No se pudo borrar', e instanceof Error ? e.message : undefined),
    );
  };

  const alElegirSugerencia = (id: IdDeSugerencia) => {
    if (id === 'subir') campo.current?.elegirArchivos();
    else if (id === 'buscar') {
      // «Busca a un cliente» mandado tal cual buscaría a alguien llamado «un cliente»: se deja
      // la frase empezada para que la persona ponga el nombre.
      setTexto('Busca a ');
      campo.current?.enfocar();
    } else despachar(id === 'me-toca' ? '¿Qué me toca hoy?' : '¿Qué puedes hacer?', []);
  };

  // ───────────── «Tu mañana» y «Tu cierre» ─────────────
  const abiertos = asuntos.data?.asuntos ?? [];
  const vencidos = abiertos.filter((a) => a.vencido).length;
  const cuantasSenales = senales.data?.senales.length ?? 0;
  const manana = asuntos.isPending
    ? 'Revisando…'
    : asuntos.isError
      ? 'No pude revisar tus pendientes.'
      : vencidos > 0
        ? `Tienes ${vencidos} ${vencidos === 1 ? 'cosa vencida' : 'cosas vencidas'} y ${abiertos.length} abiertas.`
        : `Tienes ${abiertos.length} ${abiertos.length === 1 ? 'cosa abierta' : 'cosas abiertas'}. Nada vencido.`;
  const cierre =
    ahora.getHours() < 18 ? 'El cierre del día llega a las 9:00 p. m.' : `${cuantasSenales} ${cuantasSenales === 1 ? 'cosa se te escapó' : 'cosas se te escaparon'} hoy.`;

  const historialAbierto = historial === 'abierto';

  return (
    <div className="pantalla-completa flex overflow-hidden selection:bg-brand-200 selection:text-brand-900 dark:selection:bg-brand-700 dark:selection:text-white">
      <section
        aria-label="Conversación con Athena"
        className="fondo-athena relative flex min-w-0 flex-1 flex-col"
        onDragEnter={(e) => {
          if (!traeArchivos(e.dataTransfer.types)) return;
          e.preventDefault();
          setSoltando(true);
        }}
        onDragOver={(e) => traeArchivos(e.dataTransfer.types) && e.preventDefault()}
      >
        <header className="flex h-14 shrink-0 items-center gap-2.5 border-b border-surface-border bg-surface/70 pl-3 pr-1.5 md:pl-5 md:pr-3">
          <AvatarDeAthena />
          <h1 className="min-w-0 flex-1 truncate text-[15px] leading-tight text-slate-900 dark:text-slate-50">
            <span className="font-semibold">Athena</span>
            <span aria-hidden className="px-1.5 text-slate-400">
              ·
            </span>
            <span data-titulo className="text-slate-600 dark:text-slate-300">
              {titulo}
            </span>
          </h1>
          {hiloId && (
            <button type="button" onClick={() => abrir(null)} aria-label="Nueva conversación" title="Nueva conversación" className={DE_CABECERA}>
              <SquarePen aria-hidden className="h-[18px] w-[18px]" />
              <span className="hidden lg:inline">Nueva</span>
            </button>
          )}
          {/* Dos botones para lo mismo: en escritorio pliega la columna (y se recuerda); en el teléfono abre el panel. */}
          <button
            type="button"
            onClick={() => setHistorial(historialAbierto ? 'cerrado' : 'abierto')}
            aria-label="Historial"
            aria-expanded={historialAbierto}
            title={historialAbierto ? 'Plegar el historial' : 'Abrir el historial'}
            className={cn(DE_CABECERA, 'hidden md:inline-flex')}
          >
            {historialAbierto ? <PanelRightClose aria-hidden className="h-[18px] w-[18px]" /> : <PanelRightOpen aria-hidden className="h-[18px] w-[18px]" />}
            <span className="hidden lg:inline">Historial</span>
          </button>
          <button type="button" onClick={() => setPanelEnTelefono(true)} aria-label="Historial" aria-expanded={panelEnTelefono} className={cn(DE_CABECERA, 'md:hidden')}>
            <PanelRightOpen aria-hidden className="h-5 w-5" />
          </button>
        </header>

        <div ref={rollo} className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {vacia ? (
            <div className="mx-auto flex min-h-full w-full max-w-[720px] flex-col justify-center gap-5 px-4 py-6 md:gap-7 md:px-6 md:py-10">
              <div>
                {/* En el teléfono el avatar de la cabecera queda justo encima: dos seguidos sobran. */}
                <AvatarDeAthena className="mb-4 hidden h-11 w-11 md:grid [&>svg]:h-[22px] [&>svg]:w-[22px]" />
                <h2 className="text-balance text-[1.75rem] font-bold leading-[1.15] tracking-tight text-slate-900 md:text-4xl dark:text-slate-50">
                  {saludoDe(ahora.getHours(), nombre)}
                </h2>
                <p className="mt-2 max-w-[52ch] text-[15px] leading-relaxed text-slate-600 md:text-base dark:text-slate-300">
                  Pregúntame por tu día, búscame un cliente o súbeme un archivo y lo llevo a su lugar.
                </p>
              </div>
              <ResumenDelDia manana={manana} cierre={cierre} />
              <Sugerencias onElegir={alElegirSugerencia} apagadas={contestando} />
            </div>
          ) : (
            <div className="mx-auto w-full max-w-[720px] space-y-6 px-4 py-6 md:px-6">
              {hiloId && hilo.isPending && (
                <div aria-hidden className="space-y-6 motion-safe:animate-pulse">
                  <div className="ml-auto h-10 w-2/5 rounded-2xl bg-brand-200/60 dark:bg-brand-900/40" />
                  <div className="flex gap-3">
                    <div className="h-8 w-8 shrink-0 rounded-full bg-slate-200 dark:bg-slate-800" />
                    <div className="h-24 w-3/4 rounded-2xl bg-slate-200/70 dark:bg-slate-800/70" />
                  </div>
                </div>
              )}
              {hiloId && hilo.isError && !noExiste && (
                <p role="alert" className="rounded-xl border border-surface-border bg-surface p-4 text-sm text-slate-700 dark:text-slate-200">
                  No pude abrir esta conversación.{' '}
                  <button type="button" onClick={() => void hilo.refetch()} className={cn('rounded font-semibold text-brand-700 underline underline-offset-2 dark:text-brand-300', FOCO)}>
                    Reintentar
                  </button>
                </p>
              )}
              {guardados.map((m) => (
                <BurbujaDeAthena
                  key={m.id}
                  mensaje={m}
                  ahora={ahora}
                  acciones={estadoDe(m.id)}
                  onAccion={(accion, indice) => alTocar(m, accion, indice)}
                  onConfirmar={(accion, indice) => importar(m, accion, indice)}
                  onCancelar={() => setPorConfirmar(null)}
                />
              ))}
              {enVuelo.map((p) => (
                <BurbujaDeAthena
                  key={p.clave}
                  mensaje={{ id: p.clave, rol: 'usuario', texto: p.texto, adjuntos: p.adjuntos, acciones: [], createdAt: p.createdAt }}
                  ahora={ahora}
                  envio={p.fallo ? 'fallo' : 'enviando'}
                  onReintentar={() => reintentar(p)}
                />
              ))}
              {contestandoAqui && <Escribiendo />}
            </div>
          )}
        </div>

        <CampoDeAthena
          ref={campo}
          texto={texto}
          onTexto={setTexto}
          cola={cola}
          onQuitar={(clave) => setCola((c) => c.filter((x) => x.clave !== clave))}
          onArchivos={(archivos) => subir(archivos)}
          onGrabacion={subirGrabacion}
          onEnviar={enviar}
          puedeEnviar={puedeEnviar}
        />
        <p className="shrink-0 px-3 pb-2 text-center text-xs text-slate-500 dark:text-slate-400">
          Athena nunca manda un WhatsApp: eso lo haces tú.
          {/* Sus ajustes son de la administración del sistema: a los demás el enlace no les sale. */}
          {veAjustes && (
            <>
              {' '}
              <button type="button" onClick={() => navegar('/inspector/ajustes')} className={cn('rounded font-medium text-brand-700 underline-offset-2 hover:underline dark:text-brand-300', FOCO)}>
                Ajustes
              </button>
            </>
          )}
        </p>

        {/* «Suelta aquí». La capa tapa toda la conversación mientras hay un archivo en el aire:
            así el «salió» y el «soltó» los recibe ella sola, sin parpadear entre los hijos. */}
        {soltando && (
          <div
            data-soltar
            className="absolute inset-0 z-20 grid place-items-center bg-brand-50/90 p-6 motion-safe:animate-fade-in dark:bg-slate-950/85"
            onDragOver={(e) => e.preventDefault()}
            onDragLeave={() => setSoltando(false)}
            onDrop={(e) => {
              e.preventDefault();
              setSoltando(false);
              subir(Array.from(e.dataTransfer.files));
            }}
          >
            <div className="pointer-events-none w-full max-w-md rounded-3xl border-2 border-dashed border-brand-400 bg-surface px-8 py-12 text-center shadow-xl shadow-brand-900/10 dark:border-brand-500">
              <FileUp aria-hidden className="mx-auto h-9 w-9 text-brand-600 dark:text-brand-300" />
              <p className="mt-3 text-xl font-bold text-slate-900 dark:text-slate-50">Suelta aquí</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">PDF, fotos, audios, videos, Excel o chats exportados. Hasta 25 MB cada uno.</p>
            </div>
          </div>
        )}
      </section>

      {panelEnTelefono && (
        <button type="button" aria-label="Cerrar el historial" tabIndex={-1} onClick={() => setPanelEnTelefono(false)} className="fixed inset-0 z-40 cursor-default bg-slate-900/50 motion-safe:animate-fade-in md:hidden" />
      )}
      <HistorialDeAthena
        hilos={hilos.data?.hilos}
        fallo={hilos.isError}
        onReintentar={() => void hilos.refetch()}
        activo={hiloId}
        ahora={ahora}
        onElegir={abrir}
        onNueva={() => abrir(null)}
        onRenombrar={renombrar}
        onBorrar={borrar}
        onCerrar={() => setPanelEnTelefono(false)}
        className={cn(
          panelEnTelefono ? 'fixed inset-y-0 right-0 z-50 flex w-[min(22rem,90vw)] shadow-2xl motion-safe:animate-slide-in-right' : 'hidden',
          'md:static md:z-auto md:w-80 md:shrink-0 md:animate-none md:shadow-none',
          historialAbierto ? 'md:flex' : 'md:hidden',
        )}
      />

      {llevando && <SelectorDeProyecto destino={llevando.destino} ocupado={ocupada !== null} onElegir={llevar} onCerrar={() => setLlevando(null)} />}
      {ficha && fila && cartera.data && <VentanaDeFicha fila={fila} etapas={cartera.data.etapas} abierta onCerrar={() => setFicha(null)} />}
    </div>
  );
}
